import type { TableBlock } from '@blogforge/shared';
import { canvasBlockStyle } from '../../helpers';

// ─────────────────────────────────────────────────────────────────
// Canvas preview for the table block — comparison grids, pricing
// tables, spec sheets, schedules.
//
// Three things worth knowing before you change anything here:
//
// 1. Blocks are never zod-parsed on the way in. BlockAdder builds them as
//    raw objects and the editor pages load posts/emails with a plain
//    JSON.parse, so the schema's `.default()`s (striped: true, density:
//    'normal', scrollable: true, rows: [], …) are ABSENT at runtime.
//    Every attr below is therefore read through a local fallback, and no
//    code assumes the arrays arrived as the rectangle the inspector keeps.
//
// 2. `stickyHeader` is a published-output feature. The theme renderer for
//    tables (web and email) is written separately; here the canvas pane is
//    the only scroller, so `position: sticky` has nothing to stick to and
//    is deliberately NOT applied. The toggle is instead surfaced honestly
//    as a heavier header hairline plus a micro-note under the table, so
//    turning it on is never a silent no-op. `scrollable` is different: the
//    `overflow-x-auto` wrapper on the canvas really does scroll.
//
// 3. Cells may hold inline HTML (`<strong>`, `<a>`, `<code>`, …). They are
//    rendered as TEXT, never through dangerouslySetInnerHTML: nothing
//    sanitises this path, and echoing author markup straight back into the
//    editor document is exactly how an editor-side injection happens. The
//    canvas therefore shows the precise characters the author typed —
//    published output sanitises cell HTML against the theme's rich-text
//    allow-list in the theme renderer.
// ─────────────────────────────────────────────────────────────────

type TableAttrs = TableBlock['attrs'];
type TableColors = NonNullable<TableAttrs['colors']>;
type Align = 'left' | 'center' | 'right';

const ALIGN_CLASS: Record<Align, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * Widest row across headers, body rows and footer rows. Short rows are padded
 * with empty cells while rendering, so a jagged document still previews as the
 * rectangle the author expects (published renderers do the same).
 */
function gridWidth(headers: string[], rows: string[][], footerRows: string[][]): number {
  return Math.max(
    headers.length,
    ...rows.map((row) => row.length),
    ...footerRows.map((row) => row.length),
    0,
  );
}

export function TableRenderer({ block }: { block: TableBlock }) {
  const attrs = block.attrs;

  // ── Defensive reads: every one of these is optional at runtime ──
  const headers = attrs?.headers ?? [];
  const rows = attrs?.rows ?? [];
  const footerRows = attrs?.footerRows ?? [];
  const caption = attrs?.caption;
  const captionAtBottom = attrs?.captionPosition === 'bottom';
  const striped = attrs?.striped ?? true;
  const bordered = attrs?.bordered ?? false;
  const stickyHeader = attrs?.stickyHeader ?? false;
  const scrollable = attrs?.scrollable ?? true;
  const density = attrs?.density ?? 'normal';
  const verticalAlign = attrs?.verticalAlign ?? 'middle';
  const headerColumn = attrs?.headerColumn ?? false;
  const align = attrs?.align ?? [];
  const columnWidths = attrs?.columnWidths ?? [];
  const colors: TableColors = attrs?.colors ?? {};

  const width = gridWidth(headers, rows, footerRows);
  const hasBody = rows.length > 0 || footerRows.length > 0;
  // Empty means "nothing to draw at all" — an author who has only footer rows
  // (a source line, say) still gets a real table.
  const isEmpty = headers.length === 0 && rows.length === 0 && footerRows.length === 0;

  const cellPadding =
    density === 'compact' ? 'px-3 py-1.5' : density === 'roomy' ? 'px-5 py-3.5' : 'px-3 py-2.5';
  const verticalClass =
    verticalAlign === 'top'
      ? 'align-top'
      : verticalAlign === 'bottom'
        ? 'align-bottom'
        : 'align-middle';

  const cellsOf = (row: string[]): string[] =>
    Array.from({ length: width }, (_, c) => row[c] ?? '');

  const alignClassAt = (c: number): string => ALIGN_CLASS[align[c] ?? 'left'];
  const widthAt = (c: number): string | undefined => {
    const w = columnWidths[c];
    return w ? w : undefined;
  };

  const borderColorStyle = colors.border ? { borderColor: colors.border } : undefined;

  // `bordered` controls how many sides are drawn; `colors.border` only
  // recolours the lines the layout already draws (it never turns borders on).
  // `doc-border` takes its colour from the document's `colorBorder` token — the
  // email renderer draws header, body and footer with that single colour, so the
  // preview does not distinguish them by shade either (only by width).
  const bodyBorder = bordered ? 'border doc-border' : 'border-b doc-border';
  const headBorder = bordered
    ? 'border doc-border'
    : stickyHeader
      ? 'border-b-2 doc-border'
      : 'border-b doc-border';
  // Footer rows wear the same surface as the header so totals read as part of
  // the frame rather than as one more body row — the email renderer gives its
  // footer `colorSurface` too. There is no `footerBg` in the schema, so this one
  // is a token default, not a style the author can set — `colors.stripe` is
  // deliberately NOT used here, it means "shade alternate body rows" and
  // nothing else.
  const footBorder = bordered ? 'border doc-border' : 'border-b doc-border';

  const captionNode = caption ? (
    <p
      className="text-muted text-xs"
      style={colors.caption ? { color: colors.caption } : undefined}
    >
      {caption}
    </p>
  ) : null;

  const table = (
    <table className="doc-text w-full border-collapse text-sm">
      {width > 0 && (
        <colgroup>
          {Array.from({ length: width }, (_, c) => (
            <col key={c} style={widthAt(c) ? { width: widthAt(c) } : undefined} />
          ))}
        </colgroup>
      )}

      {headers.length > 0 && (
        <thead>
          <tr>
            {cellsOf(headers).map((cell, c) => (
              <th
                key={c}
                scope="col"
                className={`${cellPadding} ${verticalClass} ${headBorder} ${alignClassAt(c)} doc-surface doc-text font-semibold`}
                style={{
                  ...(colors.headerBg ? { backgroundColor: colors.headerBg } : {}),
                  ...(colors.headerText ? { color: colors.headerText } : {}),
                  ...borderColorStyle,
                }}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
      )}

      {rows.length > 0 && (
        <tbody>
          {rows.map((row, r) => {
            const isStripe = striped && r % 2 === 1;
            // Inline styles win over these token classes, so a `colors.stripe`
            // override simply outranks the recessed-stone default.
            const rowStyle = {
              ...(isStripe && colors.stripe ? { backgroundColor: colors.stripe } : {}),
              ...borderColorStyle,
            };
            const rowClass = isStripe ? 'doc-surface-soft' : '';
            return (
              <tr key={r} className={rowClass}>
                {cellsOf(row).map((cell, c) =>
                  headerColumn && c === 0 ? (
                    <th
                      key={c}
                      scope="row"
                      className={`${cellPadding} ${verticalClass} ${bodyBorder} ${alignClassAt(c)} doc-text font-semibold`}
                      style={rowStyle}
                    >
                      {cell}
                    </th>
                  ) : (
                    <td
                      key={c}
                      className={`${cellPadding} ${verticalClass} ${bodyBorder} ${alignClassAt(c)} doc-text`}
                      style={rowStyle}
                    >
                      {cell}
                    </td>
                  ),
                )}
              </tr>
            );
          })}
        </tbody>
      )}

      {footerRows.length > 0 && (
        <tfoot>
          {footerRows.map((row, r) => (
            <tr key={r}>
              {cellsOf(row).map((cell, c) => (
                <td
                  key={c}
                  className={`${cellPadding} ${verticalClass} ${footBorder} ${alignClassAt(c)} doc-surface doc-text font-semibold`}
                  style={borderColorStyle}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  );

  return (
    // The block's own style, where the email renderer puts it too: on the
    // wrapper around the table, not on the table itself (whose cells carry the
    // block's `bordered` and colour attrs instead).
    <div className="space-y-3" style={canvasBlockStyle(block.style)}>
      {block.content && <h3 className="text-ink text-lg font-semibold">{block.content}</h3>}

      {!captionAtBottom && captionNode}

      {isEmpty ? (
        <div className="border-line bg-bg-soft text-muted rounded-lg border border-dashed p-6 text-center text-sm">
          <p className="text-ink-soft font-medium">Table block</p>
          <p className="text-muted mt-1 text-xs">
            Add headers and rows in the block settings panel on the right
          </p>
        </div>
      ) : scrollable ? (
        // Web-only affordance that genuinely works on the canvas.
        <div className="overflow-x-auto">{table}</div>
      ) : (
        table
      )}

      {captionAtBottom && captionNode}

      {stickyHeader && !isEmpty && (
        <p className="text-muted text-[11px]">
          Sticky header applies in published output — the editor canvas scrolls as a single pane, so
          it is only hinted at here.
        </p>
      )}

      {!hasBody && !isEmpty && (
        <p className="text-muted text-[11px]">
          Headers only — add a body row in the settings panel.
        </p>
      )}
    </div>
  );
}
